import React, { Suspense, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { CategoryCacheProvider } from "../contexts/CategoryCacheContext";
import { MasterDataProvider } from "../contexts/MasterDataContext";

/**
 * Application shell — Navbar on top, Sidebar + routed content side-by-side.
 *
 * <Outlet/> renders whichever route matched the URL. MasterData and
 * CategoryCache providers wrap everything so shared lookup data is cached
 * once and survives navigation between modules.
 */

function PageSpinner() {
  return (
    <div className="flex items-center justify-center h-full w-full py-16">
      <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );
}

export default function Layout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggleSidebar = () => {
    if (window.innerWidth >= 1024) {
      setSidebarCollapsed((c) => !c);
    } else {
      setSidebarOpen((o) => !o);
    }
  };

  return (
    <MasterDataProvider>
      <CategoryCacheProvider>
        <div className="flex flex-col h-screen w-screen overflow-hidden bg-gray-100">
          <Navbar onToggleSidebar={handleToggleSidebar} />

          <div className="flex flex-1 min-h-0 overflow-hidden">
            <Sidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              collapsed={sidebarCollapsed}
            />

            <main className="flex flex-col flex-1 min-w-0 min-h-0 overflow-auto">
              <Suspense fallback={<PageSpinner />}>
                <Outlet />
              </Suspense>
            </main>
          </div>
        </div>
      </CategoryCacheProvider>
    </MasterDataProvider>
  );
}
